import { Controller } from '@hotwired/stimulus'

// Searchable product picker with a "whole shop" blank option.
export default class extends Controller {
  static targets = ['input', 'hidden', 'list', 'option', 'empty', 'label']

  static values = {
    blankLabel: { type: String, default: 'Advertise my whole shop' },
    open: { type: Boolean, default: false }
  }

  connect () {
    const initialValue = this.hasHiddenTarget ? this.hiddenTarget.value : ''
    if (initialValue) {
      const selected = this.optionTargets.find(
        (option) => (option.dataset.searchableSelectValueParam || '') === String(initialValue)
      )
      this.selectedLabel = selected?.dataset?.searchableSelectLabelValue || this.blankLabelValue
    } else {
      this.selectedLabel = this.blankLabelValue
    }
    this.syncLabel()
    this.filter()
    this.onDocClick = this.onDocClick.bind(this)
    document.addEventListener('click', this.onDocClick)
  }

  disconnect () {
    document.removeEventListener('click', this.onDocClick)
  }

  open () {
    this.openValue = true
    this.listTarget.classList.remove('hidden')
    this.inputTarget.focus()
    this.filter()
  }

  close () {
    this.openValue = false
    this.listTarget.classList.add('hidden')
    this.inputTarget.value = ''
    this.syncLabel()
  }

  onDocClick (event) {
    if (!this.element.contains(event.target)) this.close()
  }

  filter () {
    const query = this.inputTarget.value.trim().toLowerCase()
    let visible = 0

    this.optionTargets.forEach((option) => {
      const text = (option.dataset.searchableSelectLabelValue || option.textContent || '').toLowerCase()
      const isBlank = option.dataset.searchableSelectBlankValue === 'true'
      const match = !query || isBlank || text.includes(query)
      option.classList.toggle('hidden', !match)
      if (match) visible += 1
    })

    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle('hidden', visible > 0)
    }
  }

  // Keep Enter/Escape inside the picker so the parent form does not submit.
  onKeydown (event) {
    if (event.key === 'Escape') {
      event.preventDefault()
      this.close()
      return
    }

    if (event.key !== 'Enter') return

    event.preventDefault()
    event.stopPropagation()

    const match = this.firstSelectableOption()
    if (match) this.applySelection(match)
  }

  firstSelectableOption () {
    const query = this.inputTarget.value.trim()
    const visible = this.optionTargets.filter((option) => !option.classList.contains('hidden'))

    if (query) {
      const product = visible.find((option) => option.dataset.searchableSelectBlankValue !== 'true')
      if (product) return product
    }

    return visible[0] || null
  }

  select (event) {
    event.preventDefault()
    this.applySelection(event.currentTarget)
  }

  applySelection (button) {
    this.hiddenTarget.value = button.dataset.searchableSelectValueParam || ''
    this.selectedLabel = button.dataset.searchableSelectLabelValue || button.textContent.trim()
    this.syncLabel()
    this.close()
  }

  clear (event) {
    event.preventDefault()
    this.hiddenTarget.value = ''
    this.selectedLabel = this.blankLabelValue
    this.syncLabel()
    this.close()
  }

  syncLabel () {
    const text = this.selectedLabel || this.blankLabelValue
    if (this.hasLabelTarget) this.labelTarget.textContent = text
    if (this.hasInputTarget) {
      this.inputTarget.placeholder = this.openValue ? 'Type to search products…' : 'Search products…'
    }
  }
}
